import { useRef } from 'react';
import { ArrowDown, ArrowUp, ImageOff, ImagePlus, Trash2, Type, Upload } from 'lucide-react';
import { BLOCK_TYPES, makeImageBlock, makeTextBlock } from '../../../database/news';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import TiptapEditor from './TiptapEditor';

const BlockShell = ({ index, total, onMoveUp, onMoveDown, onRemove, label, children }) => (
  <div className="group relative rounded-md border border-border/60 bg-background/40">
    <div className="flex items-center justify-between border-b border-border/60 px-3 py-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
        {label}
      </span>
      <div className="flex items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onMoveUp}
          disabled={index === 0}
          title="Move up"
        >
          <ArrowUp className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onMoveDown}
          disabled={index === total - 1}
          title="Move down"
        >
          <ArrowDown className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive hover:text-destructive"
          onClick={onRemove}
          title="Remove"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
    <div className="p-3">{children}</div>
  </div>
);

const TextBlock = ({ block, onChange }) => (
  <TiptapEditor
    value={block.html}
    placeholder="Write paragraph text…"
    onChange={(html) => onChange({ ...block, html })}
  />
);

const ImageBlock = ({ block, onChange, onUpload, uploading }) => {
  const fileRef = useRef(null);

  return (
    <div className="space-y-3">
      {block.url ? (
        <div className="space-y-2">
          <img
            src={block.url}
            alt={block.alt || ''}
            className="max-h-80 w-full rounded-md border border-border/60 object-cover"
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              <Upload className="h-3.5 w-3.5" />
              Replace
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ ...block, url: '', storagePath: '' })}
              className="text-destructive hover:text-destructive"
            >
              <ImageOff className="h-3.5 w-3.5" />
              Clear
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex h-40 w-full flex-col items-center justify-center gap-1 rounded-md border border-dashed border-border/60 text-xs uppercase tracking-[0.2em] text-muted-foreground transition-colors hover:border-heat hover:text-foreground"
        >
          <ImagePlus className="h-5 w-5" />
          {uploading ? 'Uploading…' : 'Choose image'}
        </button>
      )}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) await onUpload(file);
        }}
      />
      <div className="grid gap-2 sm:grid-cols-2">
        <Input
          placeholder="Alt text"
          value={block.alt || ''}
          onChange={(e) => onChange({ ...block, alt: e.target.value })}
        />
        <Textarea
          rows={1}
          placeholder="Caption (optional)"
          value={block.caption || ''}
          onChange={(e) => onChange({ ...block, caption: e.target.value })}
        />
      </div>
    </div>
  );
};

const NewsBlockEditor = ({ blocks, onChange, onUploadImage, uploadingBlockId }) => {
  const update = (idx, next) => {
    const copy = [...blocks];
    copy[idx] = next;
    onChange(copy);
  };

  const move = (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= blocks.length) return;
    const copy = [...blocks];
    [copy[idx], copy[target]] = [copy[target], copy[idx]];
    onChange(copy);
  };

  const remove = (idx) => {
    onChange(blocks.filter((_, i) => i !== idx));
  };

  const add = (type) => {
    onChange([...blocks, type === BLOCK_TYPES.IMAGE ? makeImageBlock() : makeTextBlock()]);
  };

  return (
    <div className="space-y-4">
      {blocks.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 px-6 py-10 text-center text-sm text-muted-foreground">
          No content yet — add a text or image block below.
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map((b, idx) => (
            <BlockShell
              key={b.id}
              index={idx}
              total={blocks.length}
              onMoveUp={() => move(idx, -1)}
              onMoveDown={() => move(idx, 1)}
              onRemove={() => remove(idx)}
              label={b.type === BLOCK_TYPES.IMAGE ? 'Image' : 'Text'}
            >
              {b.type === BLOCK_TYPES.IMAGE ? (
                <ImageBlock
                  block={b}
                  onChange={(next) => update(idx, next)}
                  onUpload={(file) => onUploadImage(b.id, file)}
                  uploading={uploadingBlockId === b.id}
                />
              ) : (
                <TextBlock block={b} onChange={(next) => update(idx, next)} />
              )}
            </BlockShell>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" onClick={() => add(BLOCK_TYPES.TEXT)}>
          <Type className="h-4 w-4" />
          Add text
        </Button>
        <Button type="button" variant="secondary" onClick={() => add(BLOCK_TYPES.IMAGE)}>
          <ImagePlus className="h-4 w-4" />
          Add image
        </Button>
      </div>
    </div>
  );
};

export default NewsBlockEditor;
export { BlockShell };
